import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { useClasses } from '@/hooks/useClasses/useClasses';
import { Class } from '@/models/class';
import { useSchool } from '@/providers/session/SessionProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { DialogProps } from '@toolpad/core/useDialogs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

const PLOCK_MAX = 999999;
const PLOCK_MIN = 1;

export function LessonStartDialog({ open, onClose }: DialogProps<undefined, { name: string; class: Class; passlock?: string } | null>) {
    const { t } = useTranslation();

    // state
    const [saving, setSaving] = React.useState(false);
    const [selectedClass, setSelectedClass] = React.useState<Class | null>(null);

    // error handling
    const [nameError, setNameError] = React.useState(false);
    const [nameErrorMessage, setNameErrorMessage] = React.useState('');
    const [classError, setClassError] = React.useState(false);
    const [classErrorMessage, setClassErrorMessage] = React.useState('');

    /**
     * Temporarily disable parental lock generation for Sale purpose.
     * Once we have decoupled HKCS legacy components we should re-enable this.
     */
    // const [parentalLock, setParentalLock] = React.useState<number | null>(getRandomInt(PLOCK_MIN, PLOCK_MAX));
    const [parentalLock, setParentalLock] = React.useState<number | null>();

    const { school } = useSchool();
    const { classes, loading: loadingClasses } = useClasses([{ school: { id: { equals: school?.id ?? null } } }]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!validateInputs()) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const classroomName = data.get('classroom-name') as string;
        const passlock = data.get('parental-lock') as string;

        if (!selectedClass) {
            throw new Error('No class selected');
        }

        setSaving(true);
        await onClose({
            name: classroomName,
            class: selectedClass,
            passlock,
        });
        setSaving(false);
    };

    const validateInputs = () => {
        const name = document.getElementById('classroom-name') as HTMLInputElement;

        let isValid = true;

        if (!name.value || !/^.+$/.test(name.value)) {
            setNameError(true);
            setNameErrorMessage(t('common.form.invalidLesson'));
            isValid = false;
        } else {
            setNameError(false);
            setNameErrorMessage('');
        }

        if (!selectedClass) {
            setClassError(true);
            setClassErrorMessage(t('common.form.invalidClass'));
            isValid = false;
        } else {
            setClassError(false);
            setClassErrorMessage('');
        }

        return isValid;
    };

    return (
        <Dialog fullWidth open={open} onClose={() => onClose(null)} disableRestoreFocus>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>{t('lesson.actions.startLesson')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Stack direction='column' spacing={2}>
                            <TextField
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 128,
                                        minLength: 1,
                                    },
                                }}
                                id='classroom-name'
                                name='classroom-name'
                                label={t('lesson.label.classroomName')}
                                helperText={nameErrorMessage}
                                error={nameError}
                                color={nameError ? 'error' : 'primary'}
                                required
                                fullWidth
                                autoFocus
                            />
                            <FormControl required>
                                <Autocomplete
                                    id='classes-select'
                                    loading={loadingClasses}
                                    options={loadingClasses ? [] : (classes ?? [])}
                                    getOptionLabel={(option) => option.displayName}
                                    onChange={(_event, value) => {
                                        setSelectedClass(value);
                                    }}
                                    renderInput={(params) => {
                                        return (
                                            <TextField
                                                {...params}
                                                required
                                                label={t('class.title')}
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <React.Fragment>
                                                                {loadingClasses ? <CircularProgress color='inherit' size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </React.Fragment>
                                                        ),
                                                    },
                                                    inputLabel: {
                                                        sx: {
                                                            ...(classError && {
                                                                color: 'error.main',
                                                            }),
                                                        },
                                                    },
                                                }}
                                                color={classError ? 'error' : 'primary'}
                                                sx={
                                                    classError
                                                        ? {
                                                              '& .MuiOutlinedInput-notchedOutline': {
                                                                  borderColor: 'error.main',
                                                              },
                                                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                  borderColor: 'error.main',
                                                              },
                                                              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                  borderColor: 'error.main',
                                                              },
                                                          }
                                                        : undefined
                                                }
                                            />
                                        );
                                    }}
                                    renderOption={(props, option) => {
                                        const name = option.className || '';
                                        const avatarColor = generateAvatarColor(name);
                                        const bg = avatarColor?.bg;
                                        const text = avatarColor?.text;

                                        return (
                                            <li {...props} key={option.id}>
                                                <Stack direction='row' spacing={1} alignItems='center'>
                                                    <InitialalsAvatar
                                                        sx={{
                                                            bgcolor: bg,
                                                            color: text,
                                                        }}
                                                    >
                                                        {getInitials(name)}
                                                    </InitialalsAvatar>
                                                    <Typography>{option.displayName}</Typography>
                                                </Stack>
                                            </li>
                                        );
                                    }}
                                />
                                {classError && <FormHelperText sx={{ color: 'error.main' }}>{classErrorMessage}</FormHelperText>}
                            </FormControl>
                            <TextField
                                id='parental-lock'
                                name='parental-lock'
                                label={`${t('lesson.label.parentalLock')} (${t('common.optional')})`}
                                type='number'
                                slotProps={{
                                    inputLabel: {
                                        shrink: true,
                                    },
                                }}
                                fullWidth
                                value={parentalLock || ''}
                                helperText={t('lesson.helper.parentalLock')}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (isNaN(value)) {
                                        setParentalLock(null);
                                    } else if (value >= PLOCK_MIN && value <= PLOCK_MAX) {
                                        setParentalLock(value);
                                    }
                                }}
                            />
                        </Stack>
                    </Box>
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose(null);
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} disabled={loadingClasses} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                        {t('lesson.actions.enterClassroom')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
